import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/TableContainer';
import { getAllUsers, userdelete, userfakedelete } from 'helpers/requests';
import swal from 'sweetalert';
import { Oval } from 'react-loader-spinner';

const UserManage = () => {
  const [allUserData, setAllUserData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("usr_search") || '');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("usr_sort") || 'newest');

  useEffect(() => {
    localStorage.setItem("usr_search", searchTerm);
    localStorage.setItem("usr_sort", sortOrder);
  }, [searchTerm, sortOrder]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers();
      setAllUserData(response);
    } catch (error) {
      console.error('Error fetching users:', error);
      swal('Error', 'Unable to fetch users. Please try again later.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let sortedUsers = [...allUserData];

    if (sortOrder === 'oldest') {
      sortedUsers.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else {
      sortedUsers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    if (searchTerm) {
      sortedUsers = sortedUsers.filter((user) =>
        user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredUsers(sortedUsers);
  }, [sortOrder, allUserData, searchTerm]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allUserData.map((user) => user.id));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: 'id',
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Nom Complet',
        accessorKey: 'firstname',
        cell: (cellProps) =>
          `${cellProps.row.original.firstname} ${cellProps.row.original.lastname}`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        cell: (cellProps) => cellProps.row.original.email,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Téléphone',
        accessorKey: 'phone',
        cell: (cellProps) => cellProps.row.original.phone,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Action',
        accessorKey: 'action',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-primary"
              onClick={() => handleCheckRequest(cellProps.row.original.id)}
            >
              <i className="mdi mdi-eye font-size-18" id="checktooltip" />
              <UncontrolledTooltip placement="top" target="checktooltip">
                Check Request
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(cellProps.row.original.id)}
            >
              <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
              <UncontrolledTooltip placement="top" target="deletetooltip">
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked]
  );

  const handleDelete = async (userId) => {
    swal({
      title: 'Are you sure?',
      text: 'This will hide the user, not delete permanently!',
      icon: 'warning',
      buttons: ['Cancel', 'Confirm'],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await userdelete(userId); // Update user's available field
          fetchUsers(); // Refresh the user list
          swal('User has been hidden!', { icon: 'success' });
        } catch (error) {
          swal('Error', 'Unable to hide the user.', 'error');
        }
      }
    });
  };

  const handleCheckRequest = (userId) => {
    window.location.href =`user/${userId}`
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="User Management" breadcrumbItem="Manage Users" />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Users List</CardTitle>
                <Row className="mb-3">
                  <Col md="4">
                    <FormGroup>
                      <Label for="sortSelect">Sort by</Label>
                      <Input
                        type="select"
                        id="sortSelect"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="searchInput">Search</Label>
                      <Input
                        type="text"
                        id="searchInput"
                        placeholder="Search by name or email"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '300px',
                    }}
                  >
                    <Oval
                      height={80}
                      width={80}
                      color="#556ee6"
                      visible={true}
                      ariaLabel="oval-loading"
                      secondaryColor="#4fa94d"
                      strokeWidth={2}
                      strokeWidthSecondary={2}
                    />
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={filteredUsers}
                    isLoading={false}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    title=""
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserManage;
